/**
 * PhoneChat
 *
 * Auto-playing iMessage-style chat conversation.
 * - Contact messages: typing indicator then reveal
 * - Player messages: typewriter animation
 * - Optional reply options shown in SMS UI; chosen reply is added to thread and contact reply can follow
 * - Press Space or click to skip current animation and advance
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { ChatMessage, PhoneChatReplyOption } from '../types';
import styles from './phone-chat.module.css';
import { isTypingInEditableField } from '../systems/keyboard-utils';
import { isMotionReduced } from '../systems/accessibility-settings';
import { useAccessibility } from '../systems/accessibility-context';

type ChatAnimPhase = 'typing-indicator' | 'message-delay' | 'player-typewriter' | 'completion-wait' | 'idle';

interface PhoneChatProps {
  contactName: string;
  contactAvatar?: string;
  messages: ChatMessage[];
  messageDelay?: number;   // ms between messages (default 4000)
  typingDelay?: number;    // ms for typing indicator (default 2000)
  /** When set, after messages are shown, display as reply choices in SMS UI; on pick, reply is added to thread and optional contactReply is shown. */
  replyOptions?: PhoneChatReplyOption[];
  /** Called when chat ends; if replyOptions were used, gotoScene of the chosen option is passed. */
  onComplete?: (gotoScene?: string) => void;
  /** If true, show all messages at once (for admin preview) */
  previewMode?: boolean;
}

interface DisplayedMessage {
  message: ChatMessage;
  displayedText: string;
  isComplete: boolean;
}

export const PhoneChat = ({
  contactName,
  contactAvatar,
  messages,
  messageDelay = 4000,
  typingDelay = 2000,
  replyOptions,
  onComplete,
  previewMode = false,
}: PhoneChatProps) => {
  const [displayedMessages, setDisplayedMessages] = useState<DisplayedMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  /** When set, show these as reply buttons in the SMS UI (initial or nested after a pick). */
  const [pendingReplyOptions, setPendingReplyOptions] = useState<PhoneChatReplyOption[] | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const phaseRef = useRef<ChatAnimPhase>('idle');
  const currentIndexRef = useRef(0);
  const lastSkipTimeRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const replyOptionsRef = useRef(replyOptions);
  onCompleteRef.current = onComplete;
  replyOptionsRef.current = replyOptions;
  const { strictMode: a11yStrict } = useAccessibility();
  const a11yStrictRef = useRef(a11yStrict);
  a11yStrictRef.current = a11yStrict;

  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isMotionReduced() ? 'auto' : 'smooth' });
  }, []);

  // Skip current animation / advance to next message (no-op when reply options are shown)
  const skipToNext = useCallback(() => {
    if (previewMode || pendingReplyOptions) return;
    const now = Date.now();
    if (now - lastSkipTimeRef.current < 150) return;
    lastSkipTimeRef.current = now;

    const phase = phaseRef.current;
    const idx = currentIndexRef.current;

    if (phase === 'typing-indicator') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const msg = messages[idx];
      if (msg) {
        setIsTyping(false);
        setDisplayedMessages(prev => [
          ...prev,
          { message: msg, displayedText: msg.text, isComplete: true }
        ]);
        scrollToBottom();
      }
      phaseRef.current = 'idle';
      setCurrentIndex(i => i + 1);
    } else if (phase === 'message-delay') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      phaseRef.current = 'idle';
      setCurrentIndex(i => i + 1);
    } else if (phase === 'player-typewriter') {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const msg = messages[idx];
      if (msg) {
        setDisplayedMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (!last) return prev;
          updated[updated.length - 1] = {
            ...last,
            displayedText: msg.text,
            isComplete: true,
          };
          return updated;
        });
        scrollToBottom();
      }
      phaseRef.current = 'idle';
      setCurrentIndex(i => i + 1);
    } else if (phase === 'completion-wait') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      phaseRef.current = 'idle';
      onCompleteRef.current?.();
    }
  }, [previewMode, pendingReplyOptions, messages, scrollToBottom]);

  const handleReplyOptionClick = useCallback(
    (option: PhoneChatReplyOption) => {
      if (previewMode) return;
      setPendingReplyOptions(null);
      setDisplayedMessages(prev => [
        ...prev,
        { message: { sender: 'player', text: option.text }, displayedText: option.text, isComplete: true },
      ]);
      scrollToBottom();

      const showContactReplyThenNext = () => {
        if (option.contactReply) {
          setIsTyping(true);
          scrollToBottom();
          timeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            setDisplayedMessages(prev => [
              ...prev,
              {
                message: { sender: 'contact', text: option.contactReply! },
                displayedText: option.contactReply!,
                isComplete: true,
              },
            ]);
            scrollToBottom();
            if (option.replyOptions?.length) {
              setPendingReplyOptions(option.replyOptions);
            } else if (option.goto) {
              onCompleteRef.current?.(option.goto);
            }
          }, typingDelay);
        } else {
          if (option.replyOptions?.length) {
            setPendingReplyOptions(option.replyOptions);
          } else if (option.goto) {
            onCompleteRef.current?.(option.goto);
          }
        }
      };
      showContactReplyThenNext();
    },
    [previewMode, typingDelay, scrollToBottom],
  );

  // Spacebar listener
  useEffect(() => {
    if (previewMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        if (isTypingInEditableField()) return;
        e.preventDefault();
        skipToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [skipToNext, previewMode]);

  // Preview mode: show all messages instantly
  useEffect(() => {
    if (previewMode) {
      setDisplayedMessages(
        messages.map(m => ({ message: m, displayedText: m.text, isComplete: true }))
      );
    }
  }, [previewMode, messages]);

  // Auto-play messages sequentially
  useEffect(() => {
    if (previewMode) return;
    if (currentIndex >= messages.length) {
      phaseRef.current = 'completion-wait';
      const options = replyOptionsRef.current;
      if (options?.length) {
        phaseRef.current = 'idle';
        setPendingReplyOptions(options);
      } else {
        timeoutRef.current = setTimeout(() => {
          phaseRef.current = 'idle';
          onCompleteRef.current?.();
        }, 2000);
      }
      return;
    }

    const msg = messages[currentIndex];
    if (!msg) return;

    if (msg.sender === 'contact') {
      phaseRef.current = 'typing-indicator';
      setIsTyping(true);
      scrollToBottom();

      timeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        setDisplayedMessages(prev => [
          ...prev,
          { message: msg, displayedText: msg.text, isComplete: true }
        ]);
        scrollToBottom();

        phaseRef.current = 'message-delay';
        timeoutRef.current = setTimeout(() => {
          phaseRef.current = 'idle';
          setCurrentIndex(i => i + 1);
        }, messageDelay);
      }, typingDelay);
    } else if (a11yStrictRef.current) {
      // Mode accessibilité renforcée : message joueur affiché d'un coup
      // (timeout annulable par le cleanup, pour ne pas l'ajouter deux fois).
      timeoutRef.current = setTimeout(() => {
        setDisplayedMessages(prev => [
          ...prev,
          { message: msg, displayedText: msg.text, isComplete: true }
        ]);
        scrollToBottom();
        phaseRef.current = 'idle';
        setCurrentIndex(i => i + 1);
      }, 0);
    } else {
      phaseRef.current = 'player-typewriter';
      const fullText = msg.text;
      let charIndex = 0;

      setDisplayedMessages(prev => [
        ...prev,
        { message: msg, displayedText: '', isComplete: false }
      ]);
      scrollToBottom();

      intervalRef.current = setInterval(() => {
        charIndex++;
        if (charIndex <= fullText.length) {
          setDisplayedMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (!last) return prev;
            updated[updated.length - 1] = {
              ...last,
              displayedText: fullText.slice(0, charIndex),
              isComplete: charIndex === fullText.length,
            };
            return updated;
          });
          scrollToBottom();
        } else {
          if (intervalRef.current) clearInterval(intervalRef.current);
          phaseRef.current = 'idle';
          setCurrentIndex(i => i + 1);
        }
      }, 50);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentIndex, previewMode, messages.length, messageDelay, typingDelay]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [displayedMessages, isTyping, scrollToBottom]);

  return (
    <div className={styles.chatContainer} onClick={skipToNext}>
      {/* Header */}
      <div className={styles.chatHeader}>
        <div className={styles.contactAvatar}>
          {contactAvatar ? (
            <img src={contactAvatar} alt={contactName} />
          ) : (
            contactName.charAt(0).toUpperCase()
          )}
        </div>
        <span className={styles.contactName}>{contactName}</span>
      </div>

      {/* Messages */}
      <div className={styles.messagesArea}>
        {/* Date separator */}
        <div className={styles.dateSeparator}>
          <span className={styles.dateSeparatorLabel}>Hier</span>
        </div>

        {displayedMessages.map((dm, index) => {
          const isContact = dm.message.sender === 'contact';
          return (
            <div
              key={index}
              className={`${styles.messageBubble} ${
                isContact ? styles.contactMessage : styles.playerMessage
              }`}
            >
              <p className={styles.messageText}>
                {dm.displayedText}
                {!dm.isComplete && <span className={styles.typingCursor} />}
              </p>
              {dm.message.time && dm.isComplete && (
                <span className={styles.messageTime}>{dm.message.time}</span>
              )}
            </div>
          );
        })}

        {/* Typing indicator */}
        {isTyping && (
          <div className={styles.typingIndicator}>
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
          </div>
        )}

        {/* Reply options (SMS quick replies) */}
        {pendingReplyOptions && (
          <div className={styles.replyOptions}>
            <p className={styles.replyOptionsLabel}>Choisir une réponse</p>
            {pendingReplyOptions.map((opt, i) => (
              <button
                key={i}
                type="button"
                className={styles.replyOptionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplyOptionClick(opt);
                }}
              >
                {opt.text}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
